// net-priority — fila de prioridade LEVE pro caminho de rede do app.
//
// REGRA DO DONO: "telemetria deveria ser fire-and-forget, não pode impactar a
// performance". Este módulo é o mecanismo que faz essa regra valer no NÍVEL DA
// REDE (o de main thread e transporte fica em qoe.ts).
//
// Problema medido na LG real: ao dar play/zap, o `POST /v1/stream/sign` (caminho
// CRÍTICO — é o que destrava o vídeo) às vezes ficava ATRÁS de heartbeat de
// trial e flush de QoE na fila de conexões do browser (Chr38/53 abre poucas
// conexões simultâneas por host). Custo medido: +600ms a 1,8s até o play.
//
// ===== A JANELA CRÍTICA VAI ATÉ O 1º FRAME, NÃO ATÉ O SIGN RESOLVER =====
//
// A 1ª versão deste módulo fechava a janela no `endCritical()` do sign — ou
// seja, EXATAMENTE quando o player começa a baixar o vídeo. O resultado era o
// pior caso possível: a telemetria represada era liberada TODA DE UMA VEZ bem
// no meio do buffering inicial, o trecho em que o usuário está olhando pra uma
// tela preta esperando o 1º frame. O sign parava de competir e a telemetria
// passava a competir com o VÍDEO.
//
// Agora a janela cobre o caminho crítico inteiro que o usuário espera:
//
//   beginCritical() ──sign em voo──> endCritical() ──buffering──> 1º frame
//   |<────────────────── janela crítica aberta ──────────────────>|
//                                   (hold)        notifyPlaybackStarted()
//
// Duas fontes seguram a janela:
//   1. `criticalInFlight` — há sign(s) em voo.
//   2. `playbackHold` — o sign resolveu e o player está buffering. Fecha no 1º
//      frame (`notifyPlaybackStarted`) ou no TETO DURO (`PLAYBACK_HOLD_MS`).
//
// ===== ONDE MORA O ANTI-STARVATION (mudou de lugar — de propósito) =====
//
// Antes, cada waiter de telemetria tinha um teto de 2s: "espera a janela, mas
// no máximo 2s". Isso não era prioridade, era competir com atraso — 2s cai
// bem no meio do buffering. O teto ERA o bug.
//
// Agora a garantia é estrutural: **a JANELA tem teto duro** (PLAYBACK_HOLD_MS)
// e sempre fecha sozinha. Como a janela sempre fecha, um waiter SEM teto nunca
// starva. Então:
//   - `runFireAndForget` (telemetria PURA — QoE): SEM teto. Não sai na janela
//     crítica, ponto. Perder/atrasar QoE não custa nada ao usuário.
//   - `runLowPriority` (FUNCIONAL adiável — heartbeat de trial): teto de
//     defesa em profundidade, > que o teto da janela (na prática nunca dispara;
//     só existe se um bug deixar a janela presa).
//
// ===== DRAIN ESCALONADO =====
// Ao fechar a janela, os waiters são liberados UM POR VEZ, cedendo a main
// thread entre cada (yieldToMain). Liberar N de uma vez recriava a rajada de
// conexões que este módulo existe pra evitar.
//
// Import: só `lib/idle-scheduler` (que não importa nada) → sem ciclo com
// api.ts / stream-sign.ts, que importam daqui.

import { yieldToMain } from './lib/idle-scheduler'

let criticalInFlight = 0
let playbackHoldTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Intenção de hold ACUMULADA na janela — não do último sign a sair.
 *
 * Com signs CONCORRENTES (player principal + PiP), quem chama `endCritical`
 * por último não é necessariamente quem vai tocar. Se a decisão fosse do
 * último, esta ordem perderia o hold:
 *
 *   endCritical({startPlaybackHold:true})   // A vai tocar → sai com count 2->1
 *   endCritical({startPlaybackHold:false})  // B falhou   → count 1->0 → drain
 *
 * ...e a telemetria sairia dentro do buffering de A. Como qualquer sign que
 * resolveu pra virar vídeo liga este flag, e a decisão só é tomada quando o
 * contador ZERA, o resultado passa a ser o mesmo nas duas ordens.
 */
let holdWanted = false

interface Waiter {
  release: () => void
  /** Teto próprio (defesa em profundidade). null = espera a janela, sem teto. */
  timer: ReturnType<typeof setTimeout> | null
}
let waiters: Waiter[] = []

/**
 * Teto duro do hold de buffering: quanto a janela fica aberta depois do sign
 * resolver, esperando o 1º frame.
 *
 * Números (performance.md): o critério de zapping é **<2s sustentado** e o
 * watchdog de load inicial do LivePlayerProvider dispara auto-switch aos 10s.
 * 8s cobre folgado o buffering do pior caso real (LG 2017 em Wi-Fi ruim) e
 * ainda fecha ANTES do watchdog — se o play não veio em 8s, o problema não é
 * a telemetria e não faz sentido seguir represando.
 *
 * O caminho normal nem chega perto disso: `notifyPlaybackStarted()` fecha a
 * janela no 1º frame (~300ms-2s).
 */
const PLAYBACK_HOLD_MS = 8_000

/**
 * Teto de defesa em profundidade pro tráfego FUNCIONAL adiável (heartbeat).
 * Maior que PLAYBACK_HOLD_MS de propósito: a janela sempre fecha sozinha, então
 * este teto só dispara se um bug deixar `criticalInFlight` preso. Não é o
 * mecanismo de prioridade — é o cinto de segurança dele.
 *
 * Seguro pro heartbeat: o backend calcula o tempo assistido pelo DELTA REAL
 * entre heartbeats (stream-session.ts) com clamp de 90s (HEARTBEAT_MAX_GAP_
 * SECONDS). Atrasar um heartbeat de 30s em até 15s não perde contabilidade —
 * o próximo cobre o intervalo inteiro.
 */
const DEFERRABLE_MAX_WAIT_MS = 15_000

/** True enquanto o caminho crítico (sign em voo OU buffering até o 1º frame)
 *  estiver ocupado. Exposto pra teste/diagnóstico. */
export function isCriticalInFlight(): boolean {
  return criticalInFlight > 0 || playbackHoldTimer !== null
}

function cancelPlaybackHold(): void {
  if (playbackHoldTimer) {
    clearTimeout(playbackHoldTimer)
    playbackHoldTimer = null
  }
}

function startPlaybackHold(): void {
  cancelPlaybackHold()
  playbackHoldTimer = setTimeout(() => {
    playbackHoldTimer = null
    if (!isCriticalInFlight()) void drain()
  }, PLAYBACK_HOLD_MS)
}

/** Libera os waiters represados UM POR VEZ, cedendo a main thread entre cada.
 *  Evita a rajada de N conexões simultâneas no instante em que a janela fecha. */
let draining = false
async function drain(): Promise<void> {
  if (draining) return
  draining = true
  try {
    while (waiters.length > 0) {
      // Se uma nova janela crítica abriu no meio do drain (zap seguido), PARA:
      // o resto continua represado até a próxima janela fechar.
      if (isCriticalInFlight()) return
      const next = waiters.shift()
      if (!next) break
      if (next.timer) clearTimeout(next.timer)
      next.release()
      await yieldToMain()
    }
  } finally {
    draining = false
  }
}

/** Abre uma janela crítica (um sign começou). Reentrante (contador). */
export function beginCritical(): void {
  // Um sign novo reabre a janela — o hold de buffering do sign anterior perde
  // o sentido (ex.: zap encadeado: o canal anterior nem vai tocar).
  cancelPlaybackHold()
  criticalInFlight += 1
}

/**
 * Fecha a janela do SIGN.
 *
 * `startPlaybackHold` = "este sign vai virar vídeo agora". Quando true, a janela
 * NÃO fecha: o sign resolveu e o player vai abrir a conexão de vídeo e buffering
 * — o trecho mais sensível do caminho crítico. A janela segue aberta até o 1º
 * frame (`notifyPlaybackStarted`) ou o teto duro.
 *
 * Passe false quando NÃO haverá playback: sign que FALHOU (não vai tocar nada) e
 * sign de background (prefetch/probe). Segurar 8s de hold nesses casos represa
 * telemetria sem que ninguém esteja esperando um frame — e navegar o catálogo
 * (1 prefetch por detalhe aberto) mantinha a janela permanentemente aberta,
 * matando a telemetria em vez de só adiá-la.
 *
 * A intenção é da JANELA, não deste sign: com signs concorrentes, um `true`
 * de qualquer um deles segura a janela mesmo que um `false` saia por último
 * (ver `holdWanted`). "Vai haver vídeo" é um OR entre os signs da janela.
 */
export function endCritical({ startPlaybackHold: hold = true }: { startPlaybackHold?: boolean } = {}): void {
  // Acumula ANTES do early return: quem sai no meio da janela também vota.
  if (hold) holdWanted = true
  criticalInFlight = criticalInFlight > 0 ? criticalInFlight - 1 : 0
  if (criticalInFlight > 0) return

  // Contador zerou: agora sim decide, com a intenção acumulada da janela.
  const wanted = holdWanted
  holdWanted = false
  if (wanted) {
    startPlaybackHold()
    return
  }
  // Sem playback à vista: a janela fecha de verdade agora.
  if (!isCriticalInFlight()) void drain()
}

/**
 * O caminho crítico ACABOU sem 1º frame porque ninguém está mais esperando um:
 * o app foi pra standby / a página está sendo escondida. Diferente de
 * `notifyPlaybackStarted` só na intenção — mesma mecânica.
 *
 * Existe porque, no `visibilitychange → hidden`, o flush de fim de sessão não
 * pode ficar refém de um hold de buffering de um vídeo que ninguém mais vê.
 */
export function abandonCriticalPath(): void {
  criticalInFlight = 0
  // Descarta o voto de hold da janela abortada: ele morre com ela. Sem isso o
  // voto sobreviveria ao standby e a PRÓXIMA janela (ex.: um prefetch de
  // catálogo, que fecha com `false`) herdaria um hold de 8s que ninguém pediu.
  holdWanted = false
  cancelPlaybackHold()
  void drain()
}

/**
 * O player emitiu o 1º frame ('playing') — o caminho crítico ACABOU. Fecha o
 * hold de buffering e libera a telemetria represada (drain escalonado).
 *
 * Idempotente e barato: chamar sem hold ativo é no-op. Seguro chamar de
 * qualquer player (live/vod/série/catchup).
 */
export function notifyPlaybackStarted(): void {
  // O frame veio: a intenção de hold acumulada já foi cumprida. Sem isso, um
  // sign ainda em voo fecharia depois com a intenção VELHA e abriria 8s de hold
  // esperando um frame que já pintou.
  holdWanted = false
  cancelPlaybackHold()
  if (!isCriticalInFlight()) void drain()
}

/**
 * Fila `fn` pra rodar quando o caminho crítico estiver livre.
 * `maxWaitMs = null` → espera sem teto (telemetria pura).
 */
function enqueue(maxWaitMs: number | null): Promise<void> {
  return new Promise<void>((resolve) => {
    let settled = false
    const waiter: Waiter = { release: () => {}, timer: null }
    waiter.release = (): void => {
      if (settled) return
      settled = true
      if (waiter.timer) clearTimeout(waiter.timer)
      resolve()
    }
    waiters.push(waiter)
    if (maxWaitMs !== null) {
      // Teto próprio (defesa em profundidade). `release` é idempotente, então
      // se o drain chegar depois, é no-op.
      waiter.timer = setTimeout(() => {
        waiters = waiters.filter((w) => w !== waiter)
        waiter.release()
      }, maxWaitMs)
    }
  })
}

/**
 * TELEMETRIA PURA (QoE): não sai enquanto o caminho crítico estiver ocupado.
 * SEM teto — a janela sempre fecha sozinha, então não starva. Se o usuário
 * está esperando um frame, a telemetria simplesmente não existe.
 *
 * NUNCA lança por conta da espera — só propaga o que `fn` fizer.
 */
export async function runFireAndForget<T>(fn: () => Promise<T>): Promise<T> {
  if (isCriticalInFlight()) await enqueue(null)
  return fn()
}

/**
 * FUNCIONAL ADIÁVEL (heartbeat de trial): cede a vez ao caminho crítico, mas
 * com teto de defesa em profundidade — diferente de telemetria pura, perder
 * isso tem consequência (contabilidade de trial / liveness de simultâneas).
 *
 * NUNCA lança por conta da espera — só propaga o que `fn` fizer.
 */
export async function runLowPriority<T>(fn: () => Promise<T>): Promise<T> {
  if (isCriticalInFlight()) await enqueue(DEFERRABLE_MAX_WAIT_MS)
  return fn()
}

/** Reset pra testes (zera contador, intenção de hold, hold e waiters). Não usar
 *  em produção. */
export function __resetNetPriorityForTests(): void {
  criticalInFlight = 0
  holdWanted = false
  cancelPlaybackHold()
  draining = false
  const pending = waiters
  waiters = []
  for (const w of pending) {
    if (w.timer) clearTimeout(w.timer)
    w.release()
  }
}
